// File: src/bridge/omnicast/emitSurfaceIntentV1.ts
//
// OmniCast SurfaceIntent v1 Emitter (Authority → Witness) — Phase 12.5
// -------------------------------------------------------------------
// Emits a witness-safe render intent to "omnicast:surface:intent".
// - No OmniCast imports (string event name local)
// - STRICT v1 shape (v:1)
// - Throttled + de-duped to avoid spam
//
// NOTE: This file is safe to import from OmniCine authority surfaces.

export const OMNICAST_SURFACE_INTENT_EVENT = "omnicast:surface:intent" as const;

export type OmniCastSurfaceMode = "focus" | "balanced" | "ambient";
export type SurfaceIntentKind = "cinematic" | "clarity" | "safety" | "analysis" | "ambient";

export type OmniCastSurfaceOverlayKey = "hud" | "ghosts" | "telemetry" | "sensors" | "provenance";
export type SurfaceOverlaysV1 = Partial<Record<OmniCastSurfaceOverlayKey, boolean>>;

export interface SurfaceGradeV1 {
  warmth01?: number;
  contrast01?: number;
  vignette01?: number;
  bloom01?: number;
  grain01?: number;
  ghostStrength01?: number;
  // legacy support (optional)
  ghost01?: number;
}

export interface SurfaceIntentV1 {
  sessionId?: string;
  v: 1;
  intent: SurfaceIntentKind;
  suggestMode?: OmniCastSurfaceMode;
  intensity01?: number;
  overlays?: SurfaceOverlaysV1;
  grade?: SurfaceGradeV1;
  focus?: { rect01?: { x: number; y: number; w: number; h: number }; label?: string };
  ts?: number;
  source?: string;
}

export type NarrativePhase = "setup" | "rising" | "peak" | "resolve";
export type Npc01 = { attention: number; emotion: number; overload: number; safety: number };

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function round3(n: unknown): number {
  return typeof n === "number" && Number.isFinite(n) ? Math.round(n * 1000) / 1000 : 0;
}

function stableSig(d: SurfaceIntentV1): string {
  const g = d.grade ?? {};
  const o = d.overlays ?? {};
  return [
    d.sessionId ?? "",
    d.intent,
    d.suggestMode ?? "",
    round3(d.intensity01),
    o.hud === true ? "hud:1" : o.hud === false ? "hud:0" : "hud:",
    o.ghosts === true ? "gh:1" : o.ghosts === false ? "gh:0" : "gh:",
    o.telemetry === true ? "tel:1" : o.telemetry === false ? "tel:0" : "tel:",
    o.sensors === true ? "sen:1" : o.sensors === false ? "sen:0" : "sen:",
    o.provenance === true ? "prov:1" : o.provenance === false ? "prov:0" : "prov:",
    round3(g.warmth01),
    round3(g.contrast01),
    round3(g.vignette01),
    round3(g.bloom01),
    round3(g.grain01),
    round3((g as any).ghostStrength01 ?? (g as any).ghost01),
  ].join("|");
}

function pickIntent(phase: NarrativePhase, phase01: number, npc: Npc01): SurfaceIntentKind {
  const o = clamp01(npc.overload);
  const s = clamp01(npc.safety);

  // Hard priority: safety / overload conditions
  if (s >= 0.65 || o >= 0.7) return "safety";

  // Ambient if low activation
  const activation = clamp01((npc.attention + npc.emotion) * 0.5) * clamp01(phase01);
  if (activation < 0.12) return "ambient";

  // Cinematic baseline
  return "cinematic";
}

function baseGrade(phase: NarrativePhase, phase01: number, npc: Npc01): SurfaceGradeV1 {
  const p = clamp01(phase01);
  const o = clamp01(npc.overload);

  // Conservative grade (0..1). Tuned for “dark glass” witness surfaces.
  const warmthBase = phase === "setup" ? 0.12 : phase === "rising" ? 0.30 : phase === "peak" ? 0.55 : 0.42;
  const contrastBase = phase === "setup" ? 0.14 : phase === "rising" ? 0.28 : phase === "peak" ? 0.52 : 0.22;
  const vignetteBase = phase === "peak" ? 0.52 : 0.38;

  // Damp during overload
  const damp = 1 - o * 0.55;

  const warmth01 = clamp01(warmthBase * (0.55 + p * 0.45) * damp);
  const contrast01 = clamp01(contrastBase * (0.55 + p * 0.45) * damp);
  const vignette01 = clamp01(vignetteBase * (0.70 + p * 0.30) * (0.85 + o * 0.15));

  // Ghost strength: attention/emotion, damped by overload
  const ghostStrength01 = clamp01(((npc.attention * 0.58 + npc.emotion * 0.42) * damp) * 0.35);

  return {
    warmth01,
    contrast01,
    vignette01,
    bloom01: clamp01(0.06 + contrast01 * 0.10),
    grain01: clamp01(0.04 + vignette01 * 0.08),
    ghostStrength01,
  };
}

function overlaysFor(intent: SurfaceIntentKind, npc: Npc01): SurfaceOverlaysV1 {
  const o = clamp01(npc.overload);
  if (intent === "safety") {
    return { hud: true, ghosts: false, telemetry: false, sensors: false, provenance: false };
  }
  if (intent === "ambient") {
    return { hud: false, ghosts: o < 0.35 ? true : false, telemetry: false, sensors: false, provenance: false };
  }
  // cinematic baseline
  return { hud: true, ghosts: o < 0.55 ? true : false };
}

export function createSurfaceIntentEmitter(opts?: { hz?: number; source?: string }) {
  const hz = Math.max(1, Math.min(30, opts?.hz ?? 8));
  const minIntervalMs = Math.round(1000 / hz);

  let lastEmitTs = 0;
  let lastSig = "";

  return function emitSurfaceIntent(input: {
    sessionId?: string | null;
    phase: NarrativePhase;
    phase01: number;
    npc01: Npc01;
    suggestMode?: OmniCastSurfaceMode;
  }): void {
    const now = Date.now();
    if (now - lastEmitTs < minIntervalMs) return;

    const sessionId = String(input.sessionId ?? "").trim() || undefined;
    const phase01 = clamp01(input.phase01);
    const npc01 = {
      attention: clamp01(input.npc01.attention),
      emotion: clamp01(input.npc01.emotion),
      overload: clamp01(input.npc01.overload),
      safety: clamp01(input.npc01.safety),
    };

    const intent = pickIntent(input.phase, phase01, npc01);
    const grade = baseGrade(input.phase, phase01, npc01);
    const overlays = overlaysFor(intent, npc01);

    const detail: SurfaceIntentV1 = {
      sessionId,
      v: 1,
      intent,
      suggestMode: input.suggestMode,
      intensity01: phase01,
      overlays,
      grade,
      ts: now,
      source: opts?.source ?? "timeline",
    };

    const sig = stableSig(detail);
    if (sig === lastSig) return;

    lastSig = sig;
    lastEmitTs = now;

    window.dispatchEvent(new CustomEvent(OMNICAST_SURFACE_INTENT_EVENT, { detail }));
  };
}
